/**
 * TaskDetailRenderer -- Shared rendering for task detail expand sections.
 * Single source of truth used by TaskListView and MissionDetailView.
 * Eliminates code duplication between artifacts.
 *
 * Renders: editable title, description textarea, step checklist (edit/delete/add).
 * Binds:   focusout (title+step rename), click (delete step, add step), change (checkbox).
 *
 * @module presentation/shared/TaskDetailRenderer
 */

import { TaskApiClient } from '../../infrastructure/api/TaskApiClient.js';
import { WELL_KNOWN_TAGS } from '../../domain/entities/Task.js';

// Deuda 4: esc solo cambiaba " y escHtml solo <; los dos son ahora el escape compartido.
import { esc } from './texto.js';
const escHtml = esc;

/**
 * Render tag badges for a task. Shared between TaskListView and MissionDetailView.
 */
export function renderTagBadges(tags) {
  if (!tags || tags.length === 0) return '';
  // template first
  const sorted = [...tags].sort((a, b) => (a === 'template' ? -1 : b === 'template' ? 1 : 0));
  return sorted.map(tag => {
    const known = WELL_KNOWN_TAGS[tag];
    const label = known?.label || tag;
    const color = known?.color || '#6e7681';
    return `<span class="task-tag" style="background:${color}22;color:${color}">${escHtml(label)}</span>`;
  }).join('');
}

/**
 * Normaliza un step object para manejar tanto camelCase (frontend/DB) como
 * PascalCase (API .NET serializa TaskStepDto con Name/Completed/Order).
 */
function normalizeStep(s) {
  return {
    name: s.name ?? s.Name ?? s.text ?? s.Text ?? '',
    completed: s.completed ?? s.Completed ?? false,
    order: s.order ?? s.Order ?? 0
  };
}

/**
 * Normaliza propiedades de task para manejar PascalCase de API .NET.
 * SIN esto, task.steps es undefined cuando la API devuelve task.Steps.
 */
function normalizeTask(t) {
  if (!t) return t;
  return {
    ...t,
    id: t.id ?? t.Id ?? '',
    title: t.title ?? t.Title ?? '',
    description: t.description ?? t.Description ?? '',
    steps: t.steps ?? t.Steps ?? [],
    status: t.status ?? t.Status ?? 'pending',
    tags: t.tags ?? t.Tags ?? [],
    metadata: t.metadata ?? t.Metadata ?? {},
    project: t.project ?? t.projectId ?? t.Project ?? t.ProjectId ?? ''
  };
}

// Re-exportar para uso externo
export { normalizeTask };

/**
 * Render a single step row: checkbox + editable name + delete button.
 */
export function renderStepRow(rawStep, taskId, idx) {
  const step = normalizeStep(rawStep);
  return `
    <div style="display:flex;align-items:center;gap:6px;font-size:0.875rem;color:#c9d1d9;padding:3px 0">
      <input type="checkbox" ${step.completed ? 'checked' : ''} data-step-task="${taskId}" data-step-idx="${idx}" style="cursor:pointer;accent-color:#58a6ff;flex-shrink:0">
      <input type="text" value="${esc(step.name)}" data-edit-step="${taskId}" data-step-edit-idx="${idx}"
        class="form-input" style="flex:1;min-width:0;font-size:0.875rem;padding:2px 6px;background:transparent;color:#c9d1d9;border:1px solid #21262d;${step.completed ? 'text-decoration:line-through;opacity:0.5' : ''}"
        onfocus="this.style.borderColor='#58a6ff'" onblur="this.style.borderColor='#21262d'">
      <button class="btn-icon" data-del-step="${taskId}" data-step-del-idx="${idx}" title="Eliminar paso"
        style="font-size:0.875rem;opacity:0.4;padding:2px;flex-shrink:0">&times;</button>
    </div>`;
}

/**
 * Render steps section: counter + rows + add-step input.
 */
export function renderStepsSection(steps, taskId) {
  const normalized = steps.map(normalizeStep);
  const completedSteps = normalized.filter(s => s.completed).length;

  const stepsHtml = normalized.length > 0 ? `
    <div style="margin-top:8px">
      <span style="font-size:0.875rem;color:#6e7681;font-weight:500" data-step-counter="${taskId}">Checklist (${completedSteps}/${normalized.length})</span>
      ${[...normalized].sort((a, b) => a.order - b.order).map((s, i) => renderStepRow(s, taskId, i)).join('')}
    </div>` : '';

  const addStepHtml = `
    <div style="margin-top:8px">
      <div style="display:flex;gap:6px;align-items:center">
        <input type="text" data-new-step="${taskId}" placeholder="Nuevo paso..." class="form-input" style="flex:1;font-size:0.875rem;padding:4px 8px">
        <button class="btn-ghost btn-sm" data-add-step="${taskId}" style="font-size:0.875rem;white-space:nowrap">+ Paso</button>
      </div>
    </div>`;

  return stepsHtml + addStepHtml;
}

/**
 * Render tags section: current tags with remove + input to add.
 */
function renderTagsSection(tags, taskId) {
  const tagBadges = (tags || []).map(tag => {
    const known = WELL_KNOWN_TAGS[tag];
    const label = known?.label || tag;
    const color = known?.color || '#6e7681';
    return `<span class="task-tag" style="background:${color}22;color:${color};cursor:default" data-remove-tag="${taskId}" data-tag-value="${esc(tag)}">${escHtml(label)}<span class="tag-remove" title="Quitar">&times;</span></span>`;
  }).join('');

  return `
    <div style="margin-bottom:6px">
      <label style="font-size:0.875rem;color:#6e7681;display:block;margin-bottom:4px">Tags</label>
      <div class="task-tag-list" data-tag-list="${taskId}">${tagBadges || '<span style="font-size:0.875rem;color:#484f58">Sin tags</span>'}</div>
      <div style="display:flex;gap:6px;margin-top:4px;align-items:center">
        <input type="text" data-new-tag="${taskId}" placeholder="Agregar tag..." class="form-input" style="flex:1;font-size:0.875rem;padding:2px 6px">
        <button class="btn-ghost btn-sm" data-add-tag="${taskId}" style="font-size:0.875rem">+ Tag</button>
      </div>
    </div>`;
}

/**
 * Render version section for template tasks.
 */
function renderVersionSection(task, taskId) {
  if (!(task.tags || []).includes('template')) return '';
  const version = task.metadata?.version || '';
  return `
    <div style="margin-bottom:6px;display:flex;align-items:center;gap:6px">
      <label style="font-size:0.875rem;color:#6e7681;white-space:nowrap">Version</label>
      <input type="text" data-edit-version="${taskId}" class="form-input"
        value="${esc(version)}" placeholder="1.0" style="width:80px;font-size:0.875rem;padding:2px 6px">
      <button class="btn-ghost btn-sm" data-save-version="${taskId}" style="font-size:0.875rem">Guardar</button>
    </div>`;
}

/**
 * Render full task detail section: editable title + description + steps.
 */
export function renderTaskDetailHTML(rawTask) {
  const task = normalizeTask(rawTask);
  const desc = task.description;
  const steps = task.steps;
  const taskId = task.id;

  return `
    <div style="margin-bottom:6px">
      <label style="font-size:0.875rem;color:#6e7681;display:block;margin-bottom:4px">Titulo</label>
      <input type="text" data-edit-title="${taskId}" class="form-input"
        value="${esc(task.title)}" style="width:100%;font-size:0.875rem;font-weight:500;padding:4px 8px;margin-bottom:6px">
    </div>
    <div style="margin-bottom:6px">
      <label style="font-size:0.875rem;color:#6e7681;display:block;margin-bottom:4px">Descripcion</label>
      <textarea class="form-input" data-desc-task="${taskId}" rows="2"
        style="width:100%;font-size:0.875rem;resize:vertical">${escHtml(desc)}</textarea>
      <button class="btn-ghost btn-sm" data-save-desc="${taskId}" style="margin-top:4px;font-size:0.875rem">Guardar</button>
    </div>
    ${renderTagsSection(task.tags, taskId)}
    ${renderVersionSection(task, taskId)}
    ${renderStepsSection(steps, taskId)}`;
}

/**
 * Bind task detail event handlers via event delegation.
 *
 * @param {HTMLElement} container - parent element for delegation
 * @param {Object} callbacks
 * @param {Function} [callbacks.onSaved]       - (taskId, field, value) after title/desc save
 * @param {Function} [callbacks.onStepChanged] - (taskId) after step check/rename
 * @param {Function} [callbacks.onReload]      - (taskId) after add/delete step (needs UI refresh)
 * @param {Function} [callbacks.getTask]       - (taskId) => task object from local cache (optional, avoids API call)
 * @param {Object}   [options]
 * @param {AbortSignal} [options.signal]       - AbortController signal to auto-remove listeners on abort
 */
export function bindTaskDetailEvents(container, callbacks = {}, options = {}) {
  const { onSaved, onStepChanged, onReload, getTask } = callbacks;
  const { signal } = options;

  // Helper: get task from local cache or API, always normalized
  async function fetchTask(taskId) {
    if (getTask) {
      const cached = getTask(taskId);
      if (cached) return normalizeTask(cached);
    }
    const res = await TaskApiClient.getById(taskId);
    if (!res.ok) return null;
    return normalizeTask(res.data?.data || res.data);
  }

  // --- FOCUSOUT: title rename + step rename ---
  container.addEventListener('focusout', async (e) => {
    if (signal?.aborted) return;
    // Title edit
    const titleInput = e.target.closest('[data-edit-title]');
    if (titleInput) {
      const taskId = titleInput.dataset.editTitle;
      const newTitle = titleInput.value.trim();
      if (!newTitle) return;
      const result = await TaskApiClient.update(taskId, { title: newTitle });
      if (result.ok && onSaved) onSaved(taskId, 'title', newTitle);
      return;
    }

    // Step name edit
    const editInput = e.target.closest('[data-edit-step]');
    if (editInput) {
      const taskId = editInput.dataset.editStep;
      const stepIdx = parseInt(editInput.dataset.stepEditIdx, 10);
      const newName = editInput.value.trim();
      if (!newName) return;
      const task = await fetchTask(taskId);
      if (!task?.steps) return;
      const sorted = [...task.steps].map(normalizeStep).sort((a, b) => a.order - b.order);
      if (sorted[stepIdx] && sorted[stepIdx].name !== newName) {
        sorted[stepIdx].name = newName;
        const stepsForApi = sorted.map(s => ({ name: s.name, text: s.name, completed: s.completed, order: s.order }));
        await TaskApiClient.update(taskId, { steps: stepsForApi });
        if (onStepChanged) onStepChanged(taskId);
      }
    }
  });

  // --- CLICK: delete step + add step + save desc ---
  container.addEventListener('click', async (e) => {
    if (signal?.aborted) return;
    // Delete step
    const delBtn = e.target.closest('[data-del-step]');
    if (delBtn) {
      const taskId = delBtn.dataset.delStep;
      const stepIdx = parseInt(delBtn.dataset.stepDelIdx, 10);
      const task = await fetchTask(taskId);
      if (!task?.steps) return;
      const sorted = [...task.steps].map(normalizeStep).sort((a, b) => a.order - b.order);
      sorted.splice(stepIdx, 1);
      const reordered = sorted.map((s, i) => ({ name: s.name, text: s.name, completed: s.completed, order: i }));
      await TaskApiClient.update(taskId, { steps: reordered });
      if (onReload) onReload(taskId);
      return;
    }

    // Add step
    const addBtn = e.target.closest('[data-add-step]');
    if (addBtn) {
      const taskId = addBtn.dataset.addStep;
      const input = container.querySelector(`[data-new-step="${taskId}"]`);
      const stepName = input?.value?.trim();
      if (!stepName) return;
      addBtn.disabled = true;
      const task = await fetchTask(taskId);
      if (task) {
        const existing = (task.steps || []).map(normalizeStep);
        existing.push({ name: stepName, text: stepName, completed: false, order: existing.length });
        const stepsForApi = existing.map(s => ({ name: s.name, text: s.name, completed: s.completed, order: s.order }));
        await TaskApiClient.update(taskId, { steps: stepsForApi });
        if (input) input.value = '';
        if (onReload) onReload(taskId);
      }
      addBtn.disabled = false;
      return;
    }

    // Add tag
    const addTagBtn = e.target.closest('[data-add-tag]');
    if (addTagBtn) {
      const taskId = addTagBtn.dataset.addTag;
      const input = container.querySelector(`[data-new-tag="${taskId}"]`);
      const raw = input?.value?.trim();
      if (!raw) return;
      addTagBtn.disabled = true;
      const task = await fetchTask(taskId);
      if (task) {
        const newTags = raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
        const merged = [...new Set([...(task.tags || []), ...newTags])];
        await TaskApiClient.update(taskId, { tags: merged });
        if (input) input.value = '';
        if (onReload) onReload(taskId);
      }
      addTagBtn.disabled = false;
      return;
    }

    // Remove tag
    const removeTag = e.target.closest('[data-remove-tag]');
    if (removeTag && e.target.closest('.tag-remove')) {
      const taskId = removeTag.dataset.removeTag;
      const tagValue = removeTag.dataset.tagValue;
      const task = await fetchTask(taskId);
      if (task) {
        const filtered = (task.tags || []).filter(t => t !== tagValue);
        await TaskApiClient.update(taskId, { tags: filtered });
        if (onReload) onReload(taskId);
      }
      return;
    }

    // Save version
    const saveVerBtn = e.target.closest('[data-save-version]');
    if (saveVerBtn) {
      const taskId = saveVerBtn.dataset.saveVersion;
      const input = container.querySelector(`[data-edit-version="${taskId}"]`);
      const version = input?.value?.trim();
      if (!version) return;
      saveVerBtn.disabled = true;
      const task = await fetchTask(taskId);
      if (task) {
        const merged = { ...(task.metadata || {}), version };
        await TaskApiClient.update(taskId, { metadata: merged });
        saveVerBtn.textContent = '\u2713';
        setTimeout(() => { saveVerBtn.textContent = 'Guardar'; saveVerBtn.disabled = false; }, 1200);
      } else {
        saveVerBtn.disabled = false;
      }
      return;
    }

    // Save description
    const saveBtn = e.target.closest('[data-save-desc]');
    if (saveBtn) {
      const taskId = saveBtn.dataset.saveDesc;
      const textarea = container.querySelector(`[data-desc-task="${taskId}"]`);
      const description = textarea?.value?.trim() || '';
      saveBtn.disabled = true;
      const result = await TaskApiClient.update(taskId, { description });
      if (result.ok) {
        saveBtn.textContent = '\u2713 Guardado';
        if (onSaved) onSaved(taskId, 'description', description);
        setTimeout(() => { saveBtn.textContent = 'Guardar'; saveBtn.disabled = false; }, 1500);
      } else {
        saveBtn.disabled = false;
      }
      return;
    }
  });

  // --- CHANGE: step checkbox ---
  container.addEventListener('change', async (e) => {
    if (signal?.aborted) return;
    if (!(e.target.dataset.stepTask && e.target.type === 'checkbox')) return;
    const taskId = e.target.dataset.stepTask;
    const stepIdx = parseInt(e.target.dataset.stepIdx, 10);
    const checked = e.target.checked;
    const task = await fetchTask(taskId);
    if (!task?.steps) return;
    const sorted = [...task.steps].map(normalizeStep).sort((a, b) => a.order - b.order);
    if (sorted[stepIdx] != null) {
      sorted[stepIdx].completed = checked;
      // Enviar con ambos campos (name + text) para compatibilidad BD
      const stepsForApi = sorted.map(s => ({ name: s.name, text: s.name, completed: s.completed, order: s.order }));
      await TaskApiClient.update(taskId, { steps: stepsForApi });
      const textInput = e.target.parentElement?.querySelector('[data-edit-step]');
      if (textInput) {
        textInput.style.textDecoration = checked ? 'line-through' : 'none';
        textInput.style.opacity = checked ? '0.5' : '1';
      }
      const counter = container.querySelector(`[data-step-counter="${taskId}"]`);
      if (counter) {
        const done = sorted.filter(s => s.completed).length;
        counter.textContent = `Checklist (${done}/${sorted.length})`;
      }
      if (onStepChanged) onStepChanged(taskId);
    }
  });
}
